import React, { useEffect, useState } from "react";
import { Loader2, ShieldCheck } from "lucide-react";
import { Card, CardContent } from "@/components/ui/card";
import api, { errMessage } from "@/lib/api";
import { TrendChart, PeriodToggle, Metric, VISIT_BARS } from "@/components/analytics/TrendChart";

const ACCOUNT_BARS = [
  { key: "registrations", label: "Registrations", color: "#0A2540" },
  { key: "projects_created", label: "Projects created", color: "#64748B" },
  { key: "projects_published", label: "Published", color: "#0D9488" },
];

function Section({ title, children, testId }) {
  return (
    <Card className="border-slate-200 shadow-sm" data-testid={testId}>
      <CardContent className="space-y-4 p-4 sm:p-5">
        <h3 className="text-sm font-semibold text-slate-800">{title}</h3>
        {children}
      </CardContent>
    </Card>
  );
}

function TopPages({ pages }) {
  if (!pages.length) return <p className="text-sm text-slate-500" data-testid="top-pages-empty">No genuine visits in this period yet.</p>;
  return (
    <ol className="divide-y divide-slate-100" data-testid="top-pages">
      {pages.map((p) => (
        <li key={p.project_id} className="flex items-center justify-between gap-3 py-2 text-sm" data-testid={`top-page-${p.project_id}`}>
          {p.public_id ? <a href={`/r/${p.public_id}`} target="_blank" rel="noopener noreferrer" className="truncate text-slate-800 hover:text-[#0D9488]">{p.title}</a> : <span className="truncate text-slate-400">{p.title}</span>}
          <span className="shrink-0 text-xs text-slate-500">{p.visits} visits · {p.qr_visits} QR</span>
        </li>
      ))}
    </ol>
  );
}

export function AnalyticsTab() {
  const [days, setDays] = useState(7);
  const [data, setData] = useState(null);
  const [error, setError] = useState("");
  useEffect(() => {
    setData(null); setError("");
    api.get(`/admin/analytics?days=${days}`).then((r) => setData(r.data)).catch((e) => setError(errMessage(e)));
  }, [days]);

  return (
    <div className="space-y-4" data-testid="analytics-tab">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <PeriodToggle value={days} onChange={setDays} prefix="admin-analytics" />
        <p className="flex items-start gap-1.5 text-xs text-slate-500 sm:max-w-md"><ShieldCheck className="mt-0.5 h-3.5 w-3.5 shrink-0 text-[#0D9488]" />Excludes admin and test accounts, automated traffic, and owners viewing their own pages. No IP addresses are stored.</p>
      </div>
      {error ? <p className="text-sm text-red-600" data-testid="analytics-error">{error}</p> : !data ? (
        <div className="flex justify-center py-16"><Loader2 className="h-6 w-6 animate-spin text-[#0D9488]" /></div>
      ) : (
        <>
          <div className="grid grid-cols-2 gap-2 sm:grid-cols-3 lg:grid-cols-6">
            <Metric label="Page visits" value={data.totals.visits} testId="an-visits" />
            <Metric label="QR visits" value={data.totals.qr_visits} hint="part of page visits" testId="an-qr" />
            <Metric label="Avg daily unique" value={data.totals.avg_daily_unique} hint={`peak ${data.totals.peak_daily_unique}/day · estimated`} testId="an-unique" />
            <Metric label="Registrations" value={data.totals.registrations} testId="an-registrations" />
            <Metric label="Active users" value={data.totals.active_users} hint="logged in this period" testId="an-active" />
            <Metric label="Pages published" value={data.totals.projects_published} testId="an-published" />
          </div>
          <div className="grid gap-4 lg:grid-cols-2">
            <Section title="Reference page visits" testId="an-visits-section"><TrendChart data={data.series} bars={VISIT_BARS} testId="an-visits-chart" /></Section>
            <Section title="Account activity" testId="an-accounts-section"><TrendChart data={data.series} bars={ACCOUNT_BARS} testId="an-accounts-chart" /></Section>
          </div>
          <div className="grid gap-4 lg:grid-cols-2">
            <Section title="Most visited pages" testId="an-top-section"><TopPages pages={data.top_pages} /></Section>
            <Section title="Excluded from these numbers" testId="an-excluded-section">
              <dl className="grid grid-cols-2 gap-2 text-sm" data-testid="an-excluded">
                {[["Automated traffic", data.excluded.bots], ["Owners viewing own pages", data.excluded.owner_visits],
                  ["Admin/test visitors", data.excluded.admin_or_test_visitors], ["Visits to test-account pages", data.excluded.test_account_pages],
                  ["Test account events", data.excluded.account_events], ["Events from deleted, unclassified accounts", data.excluded.deleted_account_events], ["Accounts treated as test", data.test_accounts]].map(([k, v]) => (
                  <div key={k} className="rounded bg-slate-50 px-3 py-2"><dt className="text-xs text-slate-500">{k}</dt><dd className="font-semibold text-slate-800">{v}</dd></div>
                ))}
              </dl>
              <p className="text-xs text-slate-500">Daily unique visitors use a code that resets every day, so they can't be added up into unique people across days. Only QR codes downloaded after this update are counted as QR visits; older codes still work and count as page visits.</p>
            </Section>
          </div>
        </>
      )}
    </div>
  );
}
